/**
 * @fileoverview Reusable month strip with prev/next controls.
 * @module shared/components/MonthStrip
 */

import type { ReactElement } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'

export interface MonthStripProps {
  /** Full year, e.g., 2025 */
  year: number
  /** 0-11 month index */
  monthIndex: number
  /** Called when user changes month or year */
  onChange: (year: number, monthIndex: number) => void
  /** Optional wrapper classes */
  className?: string
  /** Prevent navigation into future months */
  disableFuture?: boolean
}

/**
 * MonthStrip renders 12 months with a selected state and prev/next chevrons.
 * It handles year rollovers when navigating from Jan⇄Dec.
 *
 * @component
 * @example
 * <MonthStrip year={2025} monthIndex={8} onChange={(y,m) => setState({y,m})} />
 */
export function MonthStrip({
  year,
  monthIndex,
  onChange,
  className = '',
  disableFuture = false,
}: MonthStripProps): ReactElement {
  const { t } = useTranslation('common')
  const today = new Date()
  const currentYear = today.getFullYear()
  const currentMonth = today.getMonth()
  const months = [
    t('monthsShort.jan'),
    t('monthsShort.feb'),
    t('monthsShort.mar'),
    t('monthsShort.apr'),
    t('monthsShort.may'),
    t('monthsShort.jun'),
    t('monthsShort.jul'),
    t('monthsShort.aug'),
    t('monthsShort.sep'),
    t('monthsShort.oct'),
    t('monthsShort.nov'),
    t('monthsShort.dec'),
  ]

  const isAtLatestAllowedMonth =
    year > currentYear || (year === currentYear && monthIndex >= currentMonth)

  const prev = (): void => {
    if (monthIndex === 0) onChange(year - 1, 11)
    else onChange(year, monthIndex - 1)
  }
  const next = (): void => {
    if (disableFuture && isAtLatestAllowedMonth) return
    if (monthIndex === 11) onChange(year + 1, 0)
    else onChange(year, monthIndex + 1)
  }

  return (
    <div className={`pb-4 ${className}`}>
      <div className="min-w-0 overflow-x-hidden w-full">
        <ButtonGroup className="w-full items-center">
          <Button
            aria-label={t('monthStrip.prev')}
            variant="outline"
            size="lg"
            className="border border-border shrink-0 w-11 px-0"
            onClick={prev}
          >
            <ChevronLeft className="w-6 h-6 text-muted-foreground" />
          </Button>

          {months.map((label, idx) => {
            const active = idx === monthIndex
            const isFutureMonth = year > currentYear || (year === currentYear && idx > currentMonth)
            const futureDisabled = disableFuture && isFutureMonth
            const buttonClassName = `border text-base flex-1 min-w-0 transition-all ${
              active
                ? 'bg-muted-selector text-foreground !h-11 self-center z-10 shadow-sm ring-1 ring-inset ring-white/15 before:absolute before:inset-0 before:bg-white/[0.03] before:pointer-events-none'
                : 'border-border text-muted-foreground hover:text-muted-foreground/80'
            } ${isFutureMonth ? 'opacity-60 hover:text-muted-foreground/80' : ''} ${
              futureDisabled ? 'pointer-events-none' : ''
            }`

            return (
              <Button
                key={`${label}-${idx}`}
                type="button"
                size="lg"
                variant="outline"
                aria-pressed={active}
                aria-current={active ? 'true' : undefined}
                className={`relative overflow-hidden rounded-none ${buttonClassName}`}
                onClick={() => {
                  if (futureDisabled) return
                  onChange(year, idx)
                }}
                disabled={futureDisabled}
              >
                {isFutureMonth ? (
                  <span className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-card/30 to-transparent" />
                ) : null}
                <span className="inline-flex items-baseline gap-2 relative">
                  <span>{label}</span>
                  {active ? <span className="text-sm opacity-70">{year}</span> : null}
                </span>
              </Button>
            )
          })}

          <Button
            aria-label={t('monthStrip.next')}
            variant="outline"
            size="lg"
            className="border border-border shrink-0 w-11 px-0"
            onClick={next}
            disabled={disableFuture && isAtLatestAllowedMonth}
          >
            <ChevronRight className="w-6 h-6 text-muted-foreground" />
          </Button>
        </ButtonGroup>
      </div>
    </div>
  )
}

export default MonthStrip
